import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { asApiError, type ApiError } from '../api/client.ts';
import { catalog, scopeKey, type ListSource } from '../api/endpoints.ts';
import type { CollectionView, StorefrontItem } from '../api/types.ts';
import { useStore } from '../core/store.ts';
import { useLang } from '../i18n/index.ts';
import { addressBookStore, storefrontScope } from './address-book.ts';
import { syncCartWith } from './cart.ts';
import { applyStockTo, stockStore } from './stores.ts';

type ListData = { items: StorefrontItem[]; collection: CollectionView | null };

export type ProductList = {
  items: StorefrontItem[];
  collection: CollectionView | null;
  /** Первая загрузка: показать нечего. */
  isLoading: boolean;
  error: ApiError | null;
  reload: () => void;
};

/**
 * Товары склада, обслуживающего выбранный адрес: категория, поиск, подборка, фильтры «скидки»
 * и «новинки», избранное, покупки.
 *
 * Список читается один раз на экран; остатки дальше правит лента событий: закончившийся товар
 * сам пропадает с полки. `extraKey` перечитывает список по внешней причине (сняли сердце).
 */
export function useProductList(source: ListSource | null, extraKey = ''): ProductList {
  const lang = useLang();
  const scope = storefrontScope(useStore(addressBookStore));
  const client = useQueryClient();
  const key = ['product-list', source, scopeKey(scope), lang, extraKey];

  const query = useQuery({
    queryKey: key,
    enabled: source !== null,
    queryFn: ({ signal }): Promise<ListData> => (source ? catalog.list(source, scope, lang, signal) : Promise.resolve({ items: [], collection: null })),
    // Новый запрос поиска не должен мигать пустым списком: прежние плитки стоят до ответа.
    placeholderData: (previous) => previous,
  });

  const stock = useStore(stockStore);
  useEffect(() => {
    const event = stock.last;
    if (!event) return;
    client.setQueryData<ListData>(key, (data) => {
      if (!data) return data;
      const items = applyStockTo(data.items, event);
      return items === data.items ? data : { ...data, items, collection: data.collection ? { ...data.collection, items } : null };
    });
    // Список правится по самому событию; ключ запроса на момент события уже в замыкании.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stock.version]);

  // Пока корзина лежит, остатки кончаются, а цены меняются: каждый загруженный список сверяет её.
  const items = query.data?.items;
  useEffect(() => {
    if (items) syncCartWith(items);
  }, [items]);

  // Нет связи, а список уже на экране: старые плитки лучше ошибки.
  const error = query.error ? asApiError(query.error) : null;
  const hidesError = error?.code === 'offline' && (items?.length ?? 0) > 0;

  return {
    items: items ?? [],
    collection: query.data?.collection ?? null,
    isLoading: source !== null && query.isPending,
    error: hidesError ? null : error,
    reload: () => void query.refetch(),
  };
}
